import { IEveryDoggyAPIServices } from '../../../utils/services/EveryDoggyAPIServices';
import {
  PayPalRegisterSubscriptionRequestV2,
  PayPalRegisterSubscriptionV2Response,
} from '../../../utils/services/EveryDoggyAPITypes';
import { IErrorLoggerWithUserSupport } from '@verv-inc/js-toolkit/core-logs/lib/ErrorLoggerWithUserSupport';
import * as Sentry from '@sentry/react';
import OneLinkParametersHelper from '../../../utils/services/OneLinkParametersComposer';
import { extractEmailFromUrl } from '../../../utils/scripts/extractEmailFromUrl';
import getAmplitudeDeviceId from '../../../utils/scripts/amplitudeDeviceId';
import { attachPaypalAmplitudeDeviceId } from '../../../utils/payments/paypalAmplitudeDeviceGate';
import {
  CreatePaypalApprovalResult,
  CreatePaypalSubscriptionInput,
  RedirectStatus,
} from './types';

export interface IPayPalViewModel {
  createSubscriptionAction: (
    input: CreatePaypalSubscriptionInput
  ) => Promise<CreatePaypalApprovalResult>;
  reportPaymentError: (error: Error) => Promise<void>;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const errorWithMessage = error as { message?: string };
    if (errorWithMessage.message) {
      return errorWithMessage.message;
    }
  }

  return 'Unknown PayPal error';
};

type RedirectMetadata = {
  productId?: string;
  productName?: string;
  productAmount?: string;
  productOldPrice?: string;
  amplitudeDeviceId?: string;
  redirectFlow?: 'default' | 'chat_upsale';
  tariff?: string;
  discountShownOnce?: boolean;
};

const buildRedirectUrl = (
  sourceURL: string,
  status: RedirectStatus,
  metadata?: RedirectMetadata
): string => {
  const redirectUrl = attachPaypalAmplitudeDeviceId(
    new URL(sourceURL),
    metadata?.amplitudeDeviceId || ''
  );
  redirectUrl.searchParams.set('paypal_redirect_status', status);
  if (metadata?.productId) {
    redirectUrl.searchParams.set('paypal_product_id', metadata.productId);
  }
  if (metadata?.productName) {
    redirectUrl.searchParams.set('paypal_product_name', metadata.productName);
  }
  if (metadata?.productAmount) {
    redirectUrl.searchParams.set('paypal_product_amount', metadata.productAmount);
  }
  if (metadata?.productOldPrice) {
    redirectUrl.searchParams.set('paypal_product_old_price', metadata.productOldPrice);
  }
  if (metadata?.redirectFlow) {
    redirectUrl.searchParams.set('paypal_redirect_flow', metadata.redirectFlow);
  }
  if (metadata?.tariff) {
    redirectUrl.searchParams.set('paypal_tariff', metadata.tariff);
  }
  if (metadata?.discountShownOnce) {
    redirectUrl.searchParams.set('paypal_discount_shown', '1');
  }
  return redirectUrl.toString();
};

const normalizeRegisterResponse = (
  response: PayPalRegisterSubscriptionV2Response & { approvalLink?: string }
): CreatePaypalApprovalResult => {
  const subscriptionId = response.subscription_id || '';
  const approvalLink = response.approval_link || response.approvalLink || '';

  if (!subscriptionId) {
    const responseValidationError = new Error(
      'PayPal subscription id is missing in register response'
    );
    responseValidationError.name = 'PayPalRegisterResponseValidationError';
    Sentry.captureException(responseValidationError, {
      tags: {
        module: 'ComposePaypalViewModel.normalizeRegisterResponse',
        reason: 'missing_subscription_id',
      },
      extra: { response },
    });
    throw responseValidationError;
  }

  if (!approvalLink) {
    const responseValidationError = new Error(
      'PayPal approval link is missing in register response'
    );
    responseValidationError.name = 'PayPalRegisterResponseValidationError';
    Sentry.captureException(responseValidationError, {
      tags: {
        module: 'ComposePaypalViewModel.normalizeRegisterResponse',
        reason: 'missing_approval_link',
        subscription_id: subscriptionId,
      },
      extra: { response },
    });
    throw responseValidationError;
  }

  return {
    subscriptionId,
    approvalLink,
  };
};

function ComposePaypalViewModel(
  sourceURL: string,
  apiServices: IEveryDoggyAPIServices,
  errorLogger: IErrorLoggerWithUserSupport
): IPayPalViewModel {
  const oneLinkParametersHelper = OneLinkParametersHelper;

  const createSubscriptionAction = async (
    input: CreatePaypalSubscriptionInput
  ): Promise<CreatePaypalApprovalResult> => {
    const {
      paypalPlanId,
      paypalAmount,
      paypalName,
      internalId,
      productId,
      productOldPrice,
      redirectFlow,
      tariff,
      discountShownOnce,
      answers,
    } = input;

    const amplitudeDeviceId = getAmplitudeDeviceId();
    const redirectMetadata: RedirectMetadata = {
      productId,
      productName: paypalName,
      productAmount: paypalAmount,
      productOldPrice,
      amplitudeDeviceId,
      redirectFlow,
      tariff,
      discountShownOnce,
    };

    if (!paypalPlanId) {
      const planIdError = new Error('PayPal plan id is missing in createSubscription');
      Sentry.captureException(planIdError, {
        tags: { module: 'ComposePaypalViewModel.createSubscriptionAction' },
        extra: { sourceURL, internalId, answers },
      });
      throw planIdError;
    }

    const payload: PayPalRegisterSubscriptionRequestV2 = {
      amplitude_device_id: amplitudeDeviceId,
      paypal_plan_id: paypalPlanId,
      internal_id: internalId,
      fbclickid: oneLinkParametersHelper.getFBclickId(sourceURL),
      fbp: oneLinkParametersHelper.getFBp(sourceURL),
      source_url: sourceURL,
      redirect_urls: {
        return_url: buildRedirectUrl(sourceURL, 'return', redirectMetadata),
        cancel_url: buildRedirectUrl(sourceURL, 'cancel', redirectMetadata),
      },
      onboarding_email: extractEmailFromUrl(),
      ...(answers !== null && answers !== undefined && { onboarding_answers: answers }),
    };

    try {
      const response = await apiServices.registerPaypalSubscriptionV2(
        payload,
        'paypal',
        paypalName,
        paypalAmount
      );
      return normalizeRegisterResponse(
        response as PayPalRegisterSubscriptionV2Response & { approvalLink?: string }
      );
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'PayPalRegisterResponseValidationError') {
        throw error;
      }

      const normalizedError = new Error(
        `registerPaypalSubscriptionV2 failed: ${getErrorMessage(error)}`
      );
      normalizedError.name = 'RegisterPaypalSubscriptionError';

      Sentry.captureException(normalizedError, {
        tags: {
          module: 'ComposePaypalViewModel.createSubscriptionAction',
          paypal_plan_id: payload.paypal_plan_id || 'UNKNOWN_PLAN_ID',
        },
        extra: { payload },
      });

      throw normalizedError;
    }
  };

  const reportPaymentError = async (error: Error) => {
    errorLogger.logError(error);
  };

  return {
    createSubscriptionAction,
    reportPaymentError,
  };
}

export default ComposePaypalViewModel;
